import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Space,
  Input,
  Progress,
  Tag,
  List,
  Typography,
  Row,
  Col,
  message,
  Divider,
  Steps,
  Select,
  Modal,
  Table,
  Empty,
  DatePicker,
  Radio,
  Form,
  InputNumber,
  Image,
  Pagination,
  Spin,
  Avatar,
} from 'antd';
import dayjs from 'dayjs';
import {
  MessageOutlined,
  SendOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  HolderOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  HeartOutlined,
  LogoutOutlined,
  RobotOutlined,
  EyeOutlined,
  SearchOutlined,
  VideoCameraOutlined,
  SwapOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { fetchActiveLiveRooms, LiveRoom } from '../services/liveRoomApi';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiRequest } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const { Title, Text } = Typography;

// 商务风格色彩
const COLORS = {
  primary: '#1677ff',
  secondary: '#722ed1',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  // 兼容旧代码的别名
  cyan: '#1677ff',
  purple: '#722ed1',
  pink: '#ff4d4f',
  green: '#52c41a',
  orange: '#faad14',
  yellow: '#faad14',
  darkBg: '#f5f5f5',
  cardBg: '#ffffff',
  borderDark: '#f0f0f0',
};

interface TaskDetail {
  index: number;
  content: string;
  status: string;
  botName: string;
  sentTime: string | null;
  errorMsg: string | null;
}

interface TaskStatus {
  taskId: string;
  taskType?: string; // send_message / popularity
  roomId: number;
  status: string;
  totalCount: number;
  sentCount: number;
  joinFailedCount: number;
  sendFailedCount: number;
  progress: string;
  estimatedTime: string;
  details: TaskDetail[];
  botCount?: number;
  entryDurationSec?: number;
  stayDurationSec?: number;
  exitDurationSec?: number;
  followCount?: number;
}

interface TaskListItem {
  taskId: string;
  taskType?: string;
  roomId: number;
  status: string;
  totalCount: number;
  sentCount: number;
  joinFailedCount?: number;
  sendFailedCount?: number;
  progress: string;
  createTime: string | null;
  startTime: string | null;
  endTime: string | null;
}

type TaskMode = 'send_message' | 'popularity';

interface MessageRow {
  id: string;
  content: string;
  scheduledTime: string | null; // ISO 字符串，null 表示立即
}

const newRowId = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
  ? crypto.randomUUID()
  : `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const DEFAULT_MESSAGE_COUNT = 5;

// 任务执行流程展示组件
const TaskFlowSteps: React.FC<{
  taskType?: string;
  status?: string;
  sentCount?: number;
  totalCount?: number;
}> = ({ taskType, status, sentCount = 0, totalCount = 0 }) => {
  const isError = status === 'failed';
  const isPopularity = taskType === 'popularity';

  const items = isPopularity
    ? [
        { title: '任务排队', description: '延迟 5 秒启动', icon: <ClockCircleOutlined /> },
        { title: '气氛入场', description: '机器人在入场窗口随机加入', icon: <TeamOutlined /> },
        { title: '关注主播', description: '指定数量机器人关注主播', icon: <HeartOutlined /> },
        { title: '全部离场', description: '机器人在离场窗口陆续离开', icon: <LogoutOutlined /> },
      ]
    : [
        { title: '任务排队', description: '延迟 5 秒启动', icon: <ClockCircleOutlined /> },
        { title: '发送消息', description: '机器人依次加入房间发消息', icon: <SendOutlined /> },
        { title: '全部离场', description: '机器人陆续离开', icon: <LogoutOutlined /> },
      ];

  const getCurrentStep = () => {
    if (!status || status === 'pending') return 0;
    if (status === 'completed') return items.length - 1;
    if (status === 'failed' || status === 'cancelled') return -1;
    // running
    if (isPopularity) return 1; // running 期间精度有限，停留在 "气氛入场"
    if (sentCount < totalCount) return 1; // 还在发消息
    return 2; // Phase 1 完成，进入 全部离场
  };

  return (
    <Steps
      current={getCurrentStep()}
      status={isError ? 'error' : 'process'}
      size="small"
      items={items}
      style={{ marginBottom: 16 }}
    />
  );
};

// 简洁页面标题组件
const PageHeader: React.FC<{ icon: React.ReactNode; title: string; subtitle?: string; color?: string }> = ({
  icon, title, subtitle, color = COLORS.primary
}) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
      <div style={{
        width: 48, height: 48,
        background: `${color}10`,
        borderRadius: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 24, color: color,
      }}>{icon}</div>
      <div>
        <Title level={2} style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>{title}</Title>
        {subtitle && <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14 }}>{subtitle}</Text>}
      </div>
    </div>
    <div style={{
      width: 120, height: 3, marginLeft: 60,
      background: `linear-gradient(90deg, ${color}, ${color}66, transparent)`,
      borderRadius: 2,
    }} />
  </div>
);

// 简洁卡片组件
const SimpleCard: React.FC<{
  title: React.ReactNode;
  extra?: React.ReactNode;
  children: React.ReactNode;
  style?: React.CSSProperties;
  accentColor?: string;
}> = ({ title, extra, children, style, accentColor = COLORS.primary }) => (
  <Card
    title={<Text style={{ fontWeight: 600, fontSize: 16 }}>{title}</Text>}
    extra={extra}
    style={{
      background: COLORS.cardBg,
      border: `1px solid ${COLORS.borderDark}`,
      borderRadius: 12,
      overflow: 'hidden',
      ...style,
    }}
    styles={{
      header: {
        borderBottom: `1px solid ${COLORS.borderDark}`,
        background: `${accentColor}08`,
      },
      body: { padding: 24 },
    }}
  >
    {children}
  </Card>
);

// 可拖拽消息项组件
const SortableMessageItem: React.FC<{
  id: string;
  row: MessageRow;
  index: number;
  onChangeContent: (value: string) => void;
  onChangeTime: (value: string | null) => void;
  onDelete: () => void;
}> = ({ id, row, index, onChangeContent, onChangeTime, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <List.Item ref={setNodeRef} style={{ ...style, padding: '10px 0', border: 'none' }} {...attributes}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
        <div {...listeners} style={{ cursor: 'grab', padding: '4px' }}>
          <HolderOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />
        </div>
        <Tag color="blue">{index + 1}</Tag>
        <Input
          value={row.content}
          onChange={(e) => onChangeContent(e.target.value)}
          placeholder="填写要发送的内容"
          style={{ flex: 1 }}
        />
        <DatePicker
          showTime
          format="YYYY-MM-DD HH:mm:ss"
          placeholder="发送时间(空=立即)"
          value={row.scheduledTime ? dayjs(row.scheduledTime) : null}
          onChange={(d) => onChangeTime(d ? d.format('YYYY-MM-DD HH:mm:ss') : null)}
          style={{ width: 220 }}
          allowClear
        />
        <Button type="text" danger icon={<DeleteOutlined />} onClick={onDelete} />
      </div>
    </List.Item>
  );
};

const LiveChat: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [secretCode, setSecretCode] = useState('');
  const [selectedRoom, setSelectedRoom] = useState<LiveRoom | null>(null);

  // 直播间选择器
  const [roomPickerOpen, setRoomPickerOpen] = useState(false);
  const [rooms, setRooms] = useState<LiveRoom[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [roomTotal, setRoomTotal] = useState(0);
  const [roomPage, setRoomPage] = useState(1);
  const [roomKeyword, setRoomKeyword] = useState('');
  const ROOM_PAGE_SIZE = 50;

  const loadRooms = useCallback(async (page: number, search?: string) => {
    setLoadingRooms(true);
    try {
      const data = await fetchActiveLiveRooms(page, ROOM_PAGE_SIZE, search);
      setRooms(data.list || []);
      setRoomTotal(data.totalCount || 0);
      setRoomPage(data.currPage || page);
    } catch (e: any) {
      message.error(e.message || '加载直播间列表失败');
      setRooms([]);
      setRoomTotal(0);
    } finally {
      setLoadingRooms(false);
    }
  }, []);

  const openRoomPicker = useCallback(() => {
    setRoomPickerOpen(true);
    setRoomKeyword('');
    loadRooms(1);
  }, [loadRooms]);

  const pickRoom = useCallback((room: LiveRoom) => {
    setSelectedRoom(room);
    setRoomPickerOpen(false);
  }, []);

  // 直播间搜索 debounce
  useEffect(() => {
    if (!roomPickerOpen) return;
    const handle = setTimeout(() => loadRooms(1, roomKeyword), 300);
    return () => clearTimeout(handle);
  }, [roomKeyword, roomPickerOpen, loadRooms]);
  const [messages, setMessages] = useState<MessageRow[]>(
    () => Array.from({ length: DEFAULT_MESSAGE_COUNT }, () => ({
      id: newRowId(), content: '', scheduledTime: null,
    }))
  );

  // 任务模式 + 人气营造表单
  const [taskMode, setTaskMode] = useState<TaskMode>('send_message');
  const [popularity, setPopularity] = useState({
    botCount: 20,
    entryDurationMin: 10,
    stayDurationMin: 5,
    exitDurationMin: 20,
    followCount: 2,
  });
  const [currentTaskId, setCurrentTaskId] = useState('');
  const [taskStatus, setTaskStatus] = useState<TaskStatus | null>(null);
  const [sending, setSending] = useState(false);

  // AI 生成消息
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiModel, setAiModel] = useState<'claude' | 'deepseek'>('deepseek');
  const [aiGenerating, setAiGenerating] = useState(false);

  // 任务列表 + 创建弹框
  const [taskList, setTaskList] = useState<TaskListItem[]>([]);
  const [taskListLoading, setTaskListLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);

  const fetchTaskList = useCallback(async () => {
    const secretHeaders: Record<string, string> = !isAuthenticated && secretCode ? { 'X-Secret-Code': secretCode } : {};
    setTaskListLoading(true);
    try {
      const data = await apiRequest<any>('/api/live-chat/tasks?page=1&size=50', { headers: secretHeaders });
      if (data.code === 0) {
        setTaskList(data.data?.list || []);
      }
    } catch {
      // 静默
    } finally {
      setTaskListLoading(false);
    }
  }, [isAuthenticated, secretCode]);

  useEffect(() => {
    fetchTaskList();
  }, [fetchTaskList]);

  // 当前任务进入终态时刷一次列表
  useEffect(() => {
    if (taskStatus?.status && ['completed', 'failed', 'cancelled'].includes(taskStatus.status)) {
      fetchTaskList();
    }
  }, [taskStatus?.status, fetchTaskList]);

  const resetCreateForm = useCallback(() => {
    setSelectedRoom(null);
    setMessages(Array.from({ length: DEFAULT_MESSAGE_COUNT }, () => ({
      id: newRowId(), content: '', scheduledTime: null,
    })));
    setAiPrompt('');
    setTaskMode('send_message');
    setPopularity({ botCount: 20, entryDurationMin: 10, stayDurationMin: 5, exitDurationMin: 20, followCount: 2 });
  }, []);

  const openCreateModal = useCallback(() => {
    resetCreateForm();
    setCreateModalVisible(true);
  }, [resetCreateForm]);

  const closeCreateModal = useCallback(() => {
    if (sending || aiGenerating) return;
    setCreateModalVisible(false);
  }, [sending, aiGenerating]);

  const viewTaskDetail = useCallback((taskId: string) => {
    setCurrentTaskId(taskId);
    setTaskStatus(null);
  }, []);

  const parseAiMessages = useCallback((raw: string): string[] => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const line of raw.split(/\r?\n/)) {
      let s = line.trim();
      if (!s) continue;
      s = s.replace(/^\s*\d+\s*[.)、:：]\s*/, '');    // 去 "1." / "1)" / "1、"
      s = s.replace(/^[-*•·]\s*/, '');                 // 去 markdown 列表前缀
      s = s.replace(/^["'`]+|["'`]+$/g, '');           // 去首尾引号
      if (!s) continue;
      if (s.length > 80) continue;                     // 过长大概率混入说明
      if (seen.has(s)) continue;
      seen.add(s);
      out.push(s);
    }
    return out;
  }, []);

  const handleAiGenerate = useCallback(async () => {
    const n = messages.length;
    if (n === 0) { message.warning('请先添加至少一条消息'); return; }
    if (!aiPrompt.trim()) { message.warning('请填写提示词'); return; }

    setAiGenerating(true);
    try {
      const want = n + 3;
      const composedPrompt =
        `${aiPrompt.trim()}\n\n` +
        `请按上述要求生成 ${want} 条不同的直播间互动消息，要求：\n` +
        `- 每条 1-30 字\n` +
        `- 风格自然，像真实观众发言\n` +
        `- 互不重复\n` +
        `- 不要 emoji、特殊符号、编号\n\n` +
        `直接输出 ${want} 行，每行一条消息，不要任何编号、前缀或说明文字。`;

      const data = await apiRequest<any>('/api/ai/generate/text', {
        method: 'POST',
        body: JSON.stringify({ prompt: composedPrompt, aiType: aiModel }),
      });

      if (data.code !== 0) {
        message.error(data.msg || 'AI 生成失败');
        return;
      }
      const raw = (data.data?.content || '') as string;
      if (!raw) {
        message.error('AI 返回为空');
        return;
      }

      const parsed = parseAiMessages(raw);
      if (parsed.length === 0) {
        message.error('AI 返回内容解析失败');
        return;
      }

      // 保留旧行的 id 和 scheduledTime，只覆盖 content
      setMessages((prev) => prev.map((row, i) => ({
        ...row,
        content: parsed[i] ?? '',
      })));
      const usable = parsed.slice(0, n).filter(Boolean).length;
      if (usable < n) {
        message.warning(`AI 仅生成 ${usable} 条有效消息，剩余 ${n - usable} 个槽位为空`);
      } else {
        message.success(`已填充 ${n} 条消息`);
      }
    } catch (e: any) {
      message.error(e?.message || 'AI 生成失败');
    } finally {
      setAiGenerating(false);
    }
  }, [messages.length, aiPrompt, aiModel, parseAiMessages]);

  // 拖拽排序
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setMessages((items) => {
        const oldIndex = items.findIndex((m) => m.id === active.id);
        const newIndex = items.findIndex((m) => m.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return items;
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSend = async () => {
    if (!selectedRoom) {
      message.warning('请先选择直播间');
      return;
    }
    if (!isAuthenticated && !secretCode) {
      message.warning('请输入暗号');
      return;
    }

    let payload: Record<string, unknown>;
    if (taskMode === 'popularity') {
      const p = popularity;
      if (!p.botCount || p.botCount <= 0) { message.warning('请输入机器人数'); return; }
      if (p.followCount > p.botCount) { message.warning('关注主播数不能超过机器人数'); return; }
      payload = {
        taskType: 'popularity',
        roomId: selectedRoom.roomId,
        botCount: p.botCount,
        entryDurationSec: Math.max(0, p.entryDurationMin) * 60,
        stayDurationSec: Math.max(0, p.stayDurationMin) * 60,
        exitDurationSec: Math.max(0, p.exitDurationMin) * 60,
        followCount: Math.max(0, p.followCount),
      };
    } else {
      const validMessages = messages.filter((m) => m.content.trim());
      if (validMessages.length === 0) {
        message.warning('请输入至少一条消息');
        return;
      }
      payload = {
        taskType: 'send_message',
        roomId: selectedRoom.roomId,
        messages: validMessages.map((m) => ({
          content: m.content.trim(),
          scheduledTime: m.scheduledTime,
        })),
      };
    }

    setSending(true);
    const secretHeaders: Record<string, string> = !isAuthenticated && secretCode ? { 'X-Secret-Code': secretCode } : {};
    try {
      const data = await apiRequest<any>(`/api/live-chat/send`, {
        method: 'POST',
        headers: secretHeaders,
        body: JSON.stringify(payload),
      });
      if (data.code === 0) {
        setCurrentTaskId(data.data.taskId);
        setTaskStatus(null);
        message.success('发送任务已创建');
        setCreateModalVisible(false);
        resetCreateForm();
        fetchTaskList();
      } else {
        message.error(data.msg || '发送失败');
      }
    } catch (e: any) {
      message.error('请求失败: ' + e.message);
    } finally {
      setSending(false);
    }
  };

  const fetchTaskStatus = useCallback(async () => {
    if (!currentTaskId) return;
    const secretHeaders: Record<string, string> = !isAuthenticated && secretCode ? { 'X-Secret-Code': secretCode } : {};
    try {
      const data = await apiRequest<any>(`/api/live-chat/task/${currentTaskId}`, { headers: secretHeaders });
      if (data.code === 0) {
        setTaskStatus(data.data);
      }
    } catch (e) {
      console.error('查询任务状态失败', e);
    }
  }, [currentTaskId, isAuthenticated, secretCode]);

  useEffect(() => {
    if (!currentTaskId) return;
    fetchTaskStatus();
    const interval = setInterval(() => {
      if (['completed', 'failed', 'cancelled'].includes(taskStatus?.status || '')) {
        clearInterval(interval);
        return;
      }
      fetchTaskStatus();
    }, 3000);
    return () => clearInterval(interval);
  }, [currentTaskId, taskStatus?.status, fetchTaskStatus]);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: '#8888aa', preparing: COLORS.orange, running: COLORS.cyan,
      completed: COLORS.green, failed: COLORS.pink, cancelled: COLORS.orange, sent: COLORS.green,
    };
    return colors[status] || '#8888aa';
  };

  const getStatusText = (status: string) => {
    const texts: Record<string, string> = {
      pending: '等待中', preparing: '准备中', running: '执行中',
      completed: '已完成', failed: '失败', cancelled: '已取消', sent: '已发送',
    };
    return texts[status] || status;
  };

  // 整体任务进度（与 TaskFlowSteps 步数一致）：x/3 或 x/4
  const getOverallProgress = (t: TaskStatus): { current: number; total: number } => {
    const total = t.taskType === 'popularity' ? 4 : 3;
    if (t.status === 'completed') return { current: total, total };
    if (t.status === 'failed' || t.status === 'cancelled') return { current: 0, total };
    if (t.status === 'pending' || !t.status) return { current: 1, total };
    // running
    if (t.taskType === 'popularity') {
      // 没有更细的阶段字段，统一展示为入场中
      return { current: 2, total };
    }
    // send_message：消息全部发完后进入离场
    if ((t.sentCount ?? 0) < (t.totalCount ?? 0)) return { current: 2, total };
    return { current: total, total };
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <style>{`
        .live-chat-row-active > td { background: #e6f4ff !important; }
      `}</style>
      <PageHeader
        icon={<MessageOutlined />}
        title="直播间消息"
        subtitle="批量发送直播间消息，支持AI生成内容"
        color={COLORS.primary}
      />

      {!isAuthenticated && (
        <div style={{ marginBottom: 24 }}>
          <Text style={{ color: 'rgba(0, 0, 0, 0.65)', marginBottom: 8, display: 'block', fontSize: 14, fontWeight: 600 }}>
            <span style={{ color: COLORS.error, marginRight: 4 }}>*</span>
            暗号
          </Text>
          <Input.Password
            value={secretCode}
            onChange={(e) => setSecretCode(e.target.value)}
            placeholder="请输入暗号"
            size="large"
            style={{ maxWidth: 400 }}
          />
        </div>
      )}

      {/* 任务列表 */}
      <SimpleCard
        title={
          <Space>
            <MessageOutlined style={{ color: COLORS.primary }} />
            <span>任务列表</span>
            <Tag style={{ borderRadius: 6 }}>{taskList.length}</Tag>
          </Space>
        }
        extra={
          <Space>
            <Button icon={<SyncOutlined />} onClick={fetchTaskList} loading={taskListLoading}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              新建任务
            </Button>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        <Table<TaskListItem>
          dataSource={taskList}
          rowKey="taskId"
          loading={taskListLoading}
          size="middle"
          pagination={{ pageSize: 10, showSizeChanger: false }}
          locale={{ emptyText: <Empty description="暂无任务，点击右上角新建任务" /> }}
          rowClassName={(r) => (r.taskId === currentTaskId ? 'live-chat-row-active' : '')}
          onRow={(r) => ({
            style: { cursor: 'pointer' },
            onClick: () => viewTaskDetail(r.taskId),
          })}
          columns={[
            {
              title: '任务ID', dataIndex: 'taskId', key: 'taskId', width: 130,
              render: (v: string) => <Text code style={{ fontSize: 11 }}>{v.slice(0, 10)}…</Text>,
            },
            {
              title: '直播间', dataIndex: 'roomId', key: 'roomId', width: 110,
              render: (v: number) => <Text copyable={{ text: String(v) }}>{v}</Text>,
            },
            {
              title: '类型', dataIndex: 'taskType', key: 'taskType', width: 100,
              render: (t: string | undefined) => {
                const isPop = t === 'popularity';
                return <Tag color={isPop ? 'purple' : 'blue'}>{isPop ? '人气营造' : '发送消息'}</Tag>;
              },
            },
            {
              title: '状态', dataIndex: 'status', key: 'status', width: 100,
              render: (s: string) => (
                <Tag color={getStatusColor(s)} style={{ borderRadius: 6, fontWeight: 600 }}>
                  {getStatusText(s)}
                </Tag>
              ),
            },
            {
              title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: 170,
              render: (v: string | null) => v
                ? <Text style={{ fontSize: 12 }}>{v.replace('T', ' ').slice(0, 19)}</Text>
                : '—',
            },
            {
              title: '操作', key: 'action', width: 90,
              render: (_: unknown, r: TaskListItem) => (
                <Button
                  type="link" size="small" icon={<EyeOutlined />}
                  onClick={(e) => { e.stopPropagation(); viewTaskDetail(r.taskId); }}
                >
                  查看
                </Button>
              ),
            },
          ]}
        />
      </SimpleCard>

      {/* 新建任务 弹窗 */}
      <Modal
        title={<Space><SendOutlined style={{ color: COLORS.success }} /><span>新建任务</span></Space>}
        open={createModalVisible}
        onCancel={closeCreateModal}
        footer={null}
        width={760}
        maskClosable={!sending && !aiGenerating}
        closable={!sending && !aiGenerating}
        destroyOnClose={false}
      >
      {/* 任务类型切换 */}
      <div style={{ marginBottom: 16 }}>
        <Radio.Group value={taskMode} onChange={(e) => setTaskMode(e.target.value)} buttonStyle="solid">
          <Radio.Button value="send_message"><SendOutlined /> 发送消息</Radio.Button>
          <Radio.Button value="popularity"><TeamOutlined /> 人气营造</Radio.Button>
        </Radio.Group>
      </div>

      {/* 直播间选择 */}
      <div style={{ marginBottom: 24 }}>
        <Text style={{ color: 'rgba(0, 0, 0, 0.65)', marginBottom: 8, display: 'block', fontSize: 14, fontWeight: 600 }}>
          <span style={{ color: COLORS.error, marginRight: 4 }}>*</span>
          直播间
        </Text>
        {selectedRoom ? (
          <div
            onClick={openRoomPicker}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1677ff'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(22,119,255,0.08)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8,
              cursor: 'pointer', background: '#fff',
              transition: 'border-color 0.2s, box-shadow 0.2s',
            }}
          >
            {selectedRoom.roomImg ? (
              <Image
                src={selectedRoom.roomImg}
                width={44} height={44}
                style={{ objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                preview={false}
                fallback="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0NCIgaGVpZ2h0PSI0NCI+PC9zdmc+"
              />
            ) : (
              <div style={{ width: 44, height: 44, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, flexShrink: 0 }}>
                <VideoCameraOutlined style={{ fontSize: 20, color: '#bbb' }} />
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Text strong style={{ maxWidth: 240, fontSize: 14 }} ellipsis>
                  {selectedRoom.roomName || `直播间 #${selectedRoom.roomId}`}
                </Text>
                <Tag color="red" style={{ margin: 0, fontSize: 11, lineHeight: '16px', padding: '0 6px' }}>直播中</Tag>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#8c8c8c' }}>
                <Avatar size={16} src={selectedRoom.avatar || undefined} icon={!selectedRoom.avatar ? <UserOutlined /> : undefined} />
                <span>{selectedRoom.userName || '—'}</span>
                <span style={{ color: '#d9d9d9' }}>·</span>
                <span><EyeOutlined style={{ marginRight: 2 }} />{selectedRoom.roomUserCount ?? 0}</span>
                <span style={{ color: '#d9d9d9' }}>·</span>
                <span style={{ color: '#bfbfbf' }}>ID {selectedRoom.roomId}</span>
              </div>
            </div>
            <Button size="small" type="text" icon={<SwapOutlined />}
              onClick={(e) => { e.stopPropagation(); openRoomPicker(); }}
              style={{ color: '#1677ff', flexShrink: 0 }}
            >
              更换
            </Button>
          </div>
        ) : (
          <Button icon={<PlusOutlined />} size="large" onClick={openRoomPicker}>选择直播间</Button>
        )}
      </div>

      {taskMode === 'send_message' ? (
        <Row gutter={24}>
          <Col xs={24}>
            <SimpleCard
              title={
                <Space>
                  <SendOutlined style={{ color: COLORS.success }} />
                  <span>编辑消息</span>
                </Space>
              }
              extra={
                <Space>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => setMessages([...messages, { id: newRowId(), content: '', scheduledTime: null }])}
                    size="small"
                  >
                    添加
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => setMessages(Array.from({ length: DEFAULT_MESSAGE_COUNT }, () => ({ id: newRowId(), content: '', scheduledTime: null })))}
                    size="small"
                  >
                    重置
                  </Button>
                </Space>
              }
              style={{ marginBottom: 24 }}
              accentColor={COLORS.success}
            >
              {/* AI 生成消息 */}
              <div style={{
                background: '#f6f8fa',
                padding: 12,
                borderRadius: 6,
                border: '1px solid #eef0f3',
                marginBottom: 12,
              }}>
                <Input.TextArea
                  placeholder="提示词，例如：直播间气氛活跃，发一些和当前话题相关的轻松互动消息"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  disabled={aiGenerating}
                />
                <Row gutter={8} align="middle" style={{ marginTop: 8 }}>
                  <Col>
                    <Select
                      value={aiModel}
                      onChange={setAiModel}
                      style={{ width: 110 }}
                      disabled={aiGenerating}
                      options={[
                        { value: 'deepseek', label: 'DeepSeek' },
                        { value: 'claude', label: 'Claude' },
                      ]}
                    />
                  </Col>
                  <Col>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      将生成 <span style={{ color: COLORS.primary, fontWeight: 600 }}>{messages.length}</span> 条消息填入下方
                    </Text>
                  </Col>
                  <Col flex="auto" style={{ textAlign: 'right' }}>
                    <Button
                      icon={<RobotOutlined />}
                      onClick={handleAiGenerate}
                      loading={aiGenerating}
                      disabled={!aiPrompt.trim() || messages.length === 0}
                      type="primary"
                      ghost
                    >
                      AI 生成
                    </Button>
                  </Col>
                </Row>
              </div>

              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={messages.map((m) => m.id)} strategy={verticalListSortingStrategy}>
                  <List
                    dataSource={messages}
                    renderItem={(row, index) => (
                      <SortableMessageItem
                        key={row.id}
                        id={row.id}
                        row={row}
                        index={index}
                        onChangeContent={(value) => {
                          setMessages((items) => items.map((m, i) => i === index ? { ...m, content: value } : m));
                        }}
                        onChangeTime={(value) => {
                          setMessages((items) => items.map((m, i) => i === index ? { ...m, scheduledTime: value } : m));
                        }}
                        onDelete={() => setMessages(messages.filter((_, i) => i !== index))}
                      />
                    )}
                  />
                </SortableContext>
              </DndContext>

              <Divider style={{ margin: '16px 0' }} />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSend}
                loading={sending}
                disabled={!selectedRoom || messages.filter((m) => m.content.trim()).length === 0}
                block
                size="large"
                style={{ height: 48, fontWeight: 600, background: COLORS.success, borderColor: COLORS.success }}
              >
                确认发送
              </Button>
            </SimpleCard>
          </Col>
        </Row>
      ) : (
        <SimpleCard
          title={<Space><TeamOutlined style={{ color: COLORS.secondary }} /><span>人气营造配置</span></Space>}
          accentColor={COLORS.secondary}
          style={{ marginBottom: 24 }}
        >
          <Form layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="机器人数" required tooltip="选这么多个机器人入场">
                  <InputNumber
                    min={1} max={500}
                    value={popularity.botCount}
                    onChange={(v) => setPopularity({ ...popularity, botCount: v || 1 })}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="关注主播数" tooltip="从入场池里随机挑这么多个去关注主播">
                  <InputNumber
                    min={0} max={popularity.botCount}
                    value={popularity.followCount}
                    onChange={(v) => setPopularity({ ...popularity, followCount: v || 0 })}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="入场时长（分钟）" tooltip="所有机器人在此窗口内随机入场">
                  <InputNumber
                    min={0} max={120}
                    value={popularity.entryDurationMin}
                    onChange={(v) => setPopularity({ ...popularity, entryDurationMin: v || 0 })}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="停留时长（分钟）" tooltip="入场完成后停留多久才开始离场">
                  <InputNumber
                    min={0} max={240}
                    value={popularity.stayDurationMin}
                    onChange={(v) => setPopularity({ ...popularity, stayDurationMin: v || 0 })}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="离场时长（分钟）" tooltip="所有机器人在此窗口内随机离场">
                  <InputNumber
                    min={0} max={120}
                    value={popularity.exitDurationMin}
                    onChange={(v) => setPopularity({ ...popularity, exitDurationMin: v || 0 })}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={sending}
            disabled={!selectedRoom || !popularity.botCount}
            block
            size="large"
            style={{ height: 48, fontWeight: 600, background: COLORS.secondary, borderColor: COLORS.secondary }}
          >
            创建人气营造任务
          </Button>
        </SimpleCard>
      )}
      </Modal>

      {/* 选择直播间 弹窗 */}
      <Modal
        title="选择直播间"
        open={roomPickerOpen}
        onCancel={() => setRoomPickerOpen(false)}
        footer={null}
        width={680}
      >
        <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
          <Input
            placeholder="主播昵称、房间号、房间名"
            prefix={<SearchOutlined />}
            value={roomKeyword}
            onChange={(e) => setRoomKeyword(e.target.value)}
            style={{ width: 320 }}
            allowClear
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={() => loadRooms(1, roomKeyword)} loading={loadingRooms}>搜索</Button>
        </Space>
        {loadingRooms ? (
          <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
        ) : rooms.length === 0 ? (
          <Empty description={roomKeyword ? '没有匹配的直播间' : '暂无直播中的房间'} />
        ) : (
          <List
            style={{ maxHeight: 480, overflow: 'auto' }}
            dataSource={rooms}
            renderItem={(room) => (
              <List.Item
                key={room.roomId}
                onClick={() => pickRoom(room)}
                style={{ cursor: 'pointer', padding: '10px 8px', borderRadius: 4 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <List.Item.Meta
                  avatar={
                    room.roomImg ? (
                      <Image
                        src={room.roomImg}
                        width={56} height={56}
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                        preview={false}
                        fallback="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1NiIgaGVpZ2h0PSI1NiI+PC9zdmc+"
                      />
                    ) : (
                      <div style={{ width: 56, height: 56, background: '#d9d9d9', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4 }}>
                        <VideoCameraOutlined style={{ fontSize: 24, color: '#fff' }} />
                      </div>
                    )
                  }
                  title={
                    <Space>
                      <Text strong>{room.roomName || `直播间 #${room.roomId}`}</Text>
                      <Tag color="red">直播中</Tag>
                      <Tag color="blue"><EyeOutlined /> {room.roomUserCount ?? 0}</Tag>
                    </Space>
                  }
                  description={
                    <Space size={6} style={{ fontSize: 12 }}>
                      <Avatar size={18} src={room.avatar || undefined} icon={!room.avatar ? <UserOutlined /> : undefined} />
                      <span>{room.userName || '—'}</span>
                      {room.handle && <span style={{ color: '#888' }}>@{room.handle}</span>}
                      <span style={{ color: '#888' }}>ID: {room.roomId}</span>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
        {roomTotal > ROOM_PAGE_SIZE && (
          <div style={{ textAlign: 'right', marginTop: 12 }}>
            <Pagination
              size="small"
              current={roomPage}
              pageSize={ROOM_PAGE_SIZE}
              total={roomTotal}
              showSizeChanger={false}
              onChange={(p) => loadRooms(p, roomKeyword)}
            />
          </div>
        )}
      </Modal>

      {/* 任务详情 弹窗 */}
      <Modal
        title={
          <Space>
            <SyncOutlined spin={taskStatus?.status === 'running'} style={{ color: COLORS.secondary }} />
            <span>任务详情</span>
            {taskStatus && (
              <Tag color={getStatusColor(taskStatus.status)} style={{ borderRadius: 6, fontWeight: 600 }}>
                {getStatusText(taskStatus.status)}
              </Tag>
            )}
          </Space>
        }
        open={!!currentTaskId}
        onCancel={() => { setCurrentTaskId(''); setTaskStatus(null); }}
        footer={
          <Space>
            <Button icon={<SyncOutlined />} onClick={fetchTaskStatus}>
              刷新
            </Button>
            <Button type="primary" onClick={() => { setCurrentTaskId(''); setTaskStatus(null); }}>
              关闭
            </Button>
          </Space>
        }
        width={820}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Row gutter={16}>
            <Col span={12}>
              <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>任务ID:</Text>
              <Text style={{ marginLeft: 8 }} copyable>{currentTaskId}</Text>
            </Col>
            {taskStatus && (
              <Col span={12}>
                <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>进度:</Text>
                {(() => {
                  const p = getOverallProgress(taskStatus);
                  return (
                    <Text style={{ color: COLORS.primary, marginLeft: 8, fontWeight: 700 }}>
                      {p.current}/{p.total}
                    </Text>
                  );
                })()}
                {taskStatus.taskType !== 'popularity' && (taskStatus.totalCount ?? 0) > 0 && (
                  <Text style={{ color: 'rgba(0,0,0,0.45)', marginLeft: 8, fontSize: 12 }}>
                    （已发 {taskStatus.sentCount ?? 0}/{taskStatus.totalCount}）
                  </Text>
                )}
                {taskStatus.joinFailedCount > 0 && (
                  <Text style={{ color: COLORS.error, marginLeft: 8 }}>
                    加入失败: {taskStatus.joinFailedCount}
                  </Text>
                )}
                {taskStatus.sendFailedCount > 0 && (
                  <Text style={{ color: COLORS.error, marginLeft: 8 }}>
                    发送失败: {taskStatus.sendFailedCount}
                  </Text>
                )}
              </Col>
            )}
          </Row>
          {taskStatus && (
            <>
              <TaskFlowSteps
                taskType={taskStatus.taskType}
                status={taskStatus.status}
                sentCount={taskStatus.sentCount}
                totalCount={taskStatus.totalCount}
              />
              {taskStatus.taskType === 'popularity' ? (
                <div style={{
                  background: COLORS.darkBg,
                  borderRadius: 8,
                  padding: 14,
                  border: `1px solid ${COLORS.borderDark}`,
                }}>
                  <Row gutter={[16, 8]}>
                    <Col span={8}>
                      <Text type="secondary" style={{ fontSize: 12 }}>机器人数</Text>
                      <div style={{ fontSize: 16, fontWeight: 600 }}>{taskStatus.botCount ?? '—'}</div>
                    </Col>
                    <Col span={8}>
                      <Text type="secondary" style={{ fontSize: 12 }}>关注主播数</Text>
                      <div style={{ fontSize: 16, fontWeight: 600 }}>{taskStatus.followCount ?? '—'}</div>
                    </Col>
                    <Col span={8}>
                      <Text type="secondary" style={{ fontSize: 12 }}>入场时长</Text>
                      <div style={{ fontSize: 14 }}>{taskStatus.entryDurationSec ? `${Math.round(taskStatus.entryDurationSec / 60)} 分钟` : '—'}</div>
                    </Col>
                    <Col span={8}>
                      <Text type="secondary" style={{ fontSize: 12 }}>停留时长</Text>
                      <div style={{ fontSize: 14 }}>{taskStatus.stayDurationSec ? `${Math.round(taskStatus.stayDurationSec / 60)} 分钟` : '—'}</div>
                    </Col>
                    <Col span={8}>
                      <Text type="secondary" style={{ fontSize: 12 }}>离场时长</Text>
                      <div style={{ fontSize: 14 }}>{taskStatus.exitDurationSec ? `${Math.round(taskStatus.exitDurationSec / 60)} 分钟` : '—'}</div>
                    </Col>
                  </Row>
                </div>
              ) : (
                <Progress
                  percent={parseInt(taskStatus.progress) || 0}
                  strokeColor={COLORS.primary}
                  strokeWidth={10}
                  style={{ marginTop: 8 }}
                />
              )}
              {taskStatus.taskType !== 'popularity' && taskStatus.details && taskStatus.details.length > 0 && (
                <List
                  size="small"
                  dataSource={taskStatus.details}
                  style={{
                    maxHeight: 360,
                    overflow: 'auto',
                    background: COLORS.darkBg,
                    borderRadius: 8,
                    padding: 14,
                    border: `1px solid ${COLORS.borderDark}`,
                  }}
                  renderItem={(detail) => (
                    <List.Item style={{ padding: '10px 0', border: 'none' }}>
                      <Space style={{ width: '100%' }}>
                        <Tag color="default">{detail.index + 1}</Tag>
                        <Text style={{ flex: 1 }} ellipsis={{ tooltip: detail.content }}>
                          {detail.content}
                        </Text>
                        <Tag color={getStatusColor(detail.status)} style={{ borderRadius: 6, fontWeight: 600 }}>
                          {getStatusText(detail.status)}
                        </Tag>
                        {detail.botName && (
                          <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12 }}>{detail.botName}</Text>
                        )}
                      </Space>
                    </List.Item>
                  )}
                />
              )}
            </>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default LiveChat;
